import { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  BarChart3,
  Clock,
  Database,
  Zap,
} from 'lucide-react';

interface MetricHighlight {
  label: string;
  value: string | number;
  change?: number;
  direction?: 'up' | 'down' | 'stable';
  prefix?: string;
  suffix?: string;
}

interface InsightItem {
  text: string;
  type?: 'highlight' | 'warning' | 'info' | 'trend';
}

interface AnalyticsResponseProps {
  /** Main summary text - the "answer" to the user's question */
  summary: string;
  /** Key metrics to highlight prominently */
  metrics?: MetricHighlight[];
  /** Insights/bullet points */
  insights?: (string | InsightItem)[];
  /** Chart/visualization component */
  visualization?: React.ReactNode;
  /** Processing metadata */
  metadata?: {
    executionTimeMs?: number;
    resultCount?: number;
    confidence?: number;
    dataSource?: string;
  };
  /** Follow-up suggestions */
  suggestions?: string[];
  /** Callback when suggestion clicked */
  onSuggestionClick?: (suggestion: string) => void;
  /** Expanded mode */
  expanded?: boolean;
}

/**
 * Production-grade Analytics Response Card
 *
 * Design patterns based on:
 * - ThoughtSpot Sage/Spotter: Insight cards with metrics
 * - Cloudscape Design System: AI chat response patterns
 * - Modern dashboard UX: Visual hierarchy, metric highlights
 */
export default function AnalyticsResponse({
  summary,
  metrics = [],
  insights = [],
  visualization,
  metadata,
  suggestions = [],
  onSuggestionClick,
  expanded = false,
}: AnalyticsResponseProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleCopy = () => {
    const textToCopy = [
      summary,
      ...insights.map((i) => (typeof i === 'string' ? `• ${i}` : `• ${i.text}`)),
    ].join('\n');
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFeedback = (type: 'up' | 'down') => {
    setFeedback(feedback === type ? null : type);
    // Could send to backend for ML training
  };

  // Format insight items
  const formatInsight = (insight: string | InsightItem, index: number) => {
    const item = typeof insight === 'string' ? { text: insight, type: 'info' as const } : insight;

    const typeStyles = {
      highlight: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
      warning: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
      info: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
      trend: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
    };

    const icons = {
      highlight: <Zap className="h-3.5 w-3.5" />,
      warning: <TrendingDown className="h-3.5 w-3.5" />,
      info: <Lightbulb className="h-3.5 w-3.5" />,
      trend: <TrendingUp className="h-3.5 w-3.5" />,
    };

    return (
      <div
        key={index}
        className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm ${typeStyles[item.type || 'info']}`}
      >
        <span className="mt-0.5 shrink-0">{icons[item.type || 'info']}</span>
        <span>{item.text}</span>
      </div>
    );
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
      {/* Summary Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b">
        <p className="text-sm font-medium text-foreground leading-relaxed">
          {summary}
        </p>
      </div>

      {/* Metrics Row - Hero Numbers */}
      {metrics.length > 0 && (
        <div className={`grid gap-3 p-4 border-b bg-muted/30 ${
          metrics.length === 1 ? 'grid-cols-1' :
          metrics.length === 2 ? 'grid-cols-2' :
          metrics.length === 3 ? 'grid-cols-3' :
          'grid-cols-2 md:grid-cols-4'
        }`}>
          {metrics.map((metric, idx) => (
            <div key={idx} className="text-center">
              <div className="text-xs text-muted-foreground mb-1">{metric.label}</div>
              <div className="text-xl font-bold text-foreground">
                {metric.prefix}{typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}{metric.suffix}
              </div>
              {metric.change !== undefined && (
                <div className={`inline-flex items-center gap-0.5 text-xs mt-1 px-1.5 py-0.5 rounded-full ${
                  metric.direction === 'up'
                    ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950'
                    : metric.direction === 'down'
                    ? 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950'
                    : 'text-muted-foreground bg-muted'
                }`}>
                  {metric.direction === 'up' && <TrendingUp className="h-3 w-3" />}
                  {metric.direction === 'down' && <TrendingDown className="h-3 w-3" />}
                  {metric.direction === 'stable' && <Minus className="h-3 w-3" />}
                  <span>{metric.change > 0 && '+'}{metric.change.toFixed(1)}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Insights Section */}
      {insights.length > 0 && (
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Key Insights
            </span>
          </div>
          <div className="space-y-2">
            {insights.slice(0, showDetails ? undefined : 3).map((insight, idx) => formatInsight(insight, idx))}
          </div>
          {insights.length > 3 && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show {insights.length - 3} more insights
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Visualization */}
      {visualization && (
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Visualization
            </span>
          </div>
          <div className={expanded ? '' : 'max-h-[300px] overflow-hidden'}>
            {visualization}
          </div>
        </div>
      )}

      {/* Follow-up Suggestions */}
      {suggestions.length > 0 && (
        <div className="px-4 py-3 border-b bg-muted/20">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Try asking:</span>
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer - Metadata & Actions */}
      <div className="px-4 py-2.5 flex items-center justify-between bg-muted/10">
        {/* Metadata */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {metadata?.executionTimeMs !== undefined && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{metadata.executionTimeMs}ms</span>
            </div>
          )}
          {metadata?.resultCount !== undefined && (
            <div className="flex items-center gap-1">
              <Database className="h-3 w-3" />
              <span>{metadata.resultCount} results</span>
            </div>
          )}
          {metadata?.confidence !== undefined && metadata.confidence < 0.8 && (
            <div className="flex items-center gap-1 text-amber-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span>{Math.round(metadata.confidence * 100)}% confident</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            className={`p-1.5 rounded-md hover:bg-muted transition-colors ${
              copied ? 'text-green-600' : 'text-muted-foreground'
            }`}
            title="Copy to clipboard"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => handleFeedback('up')}
            className={`p-1.5 rounded-md hover:bg-muted transition-colors ${
              feedback === 'up' ? 'text-green-600 bg-green-100 dark:bg-green-950' : 'text-muted-foreground'
            }`}
            title="Helpful"
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleFeedback('down')}
            className={`p-1.5 rounded-md hover:bg-muted transition-colors ${
              feedback === 'down' ? 'text-red-600 bg-red-100 dark:bg-red-950' : 'text-muted-foreground'
            }`}
            title="Not helpful"
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
